import { useQuery } from '@tanstack/react-query';
import {
  getHubspotSyncCobertura,
  type HubspotSyncCobertura,
} from '@lib/services/hubspot-sync.service';

interface Props {
  padronId: string;
  /** Estado del padrón — conservado por compatibilidad con el caller. No se usa. */
  padronEstado?: string;
}

/**
 * Card para admin Medicus: muestra qué porcentaje de los titulares del padrón
 * ya tienen un contacto creado en HubSpot (= `hubspotId` guardado en Mongo).
 *
 * Antes este card también disparaba la sincronización masiva, pero el botón
 * se removió hasta resolver el caso de titulares con email compartido (HubSpot
 * dedupea por email único y mergea personas distintas en un mismo contacto).
 * La sincronización se opera ahora por scripts en `server/scripts/` —
 * `backfill-hubspot-ids.js` (linkea por id_web_am, Mongo-only) y, una vez que
 * mergee el fix del gateway, `crear-contactos-hubspot.js` para los faltantes.
 */
export function HubspotSyncCard({ padronId }: Props) {
  const cobQ = useQuery({
    queryKey: ['hubspot-sync-cobertura', padronId],
    queryFn: () => getHubspotSyncCobertura(padronId),
    enabled: !!padronId,
    retry: false,
  });

  return (
    <section
      className="card"
      style={{
        marginTop: 16,
        padding: 16,
        background: 'linear-gradient(135deg, rgba(255,122,0,0.04), rgba(255,122,0,0.08))',
        border: '1px solid rgba(255,122,0,0.18)',
      }}
    >
      <header style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <div>
          <h3 className="heading-5" style={{ margin: 0 }}>Sincronización con HubSpot</h3>
          <p className="text-element-mid" style={{ fontSize: 13, margin: '4px 0 0' }}>
            Porcentaje de titulares del padrón que ya tienen contacto creado en HubSpot.
          </p>
        </div>
      </header>

      {cobQ.isLoading && (
        <p className="text-element-mid" style={{ fontSize: 13, marginTop: 12 }}>
          Calculando cobertura…
        </p>
      )}
      {cobQ.isError && (
        <p className="status status-error" style={{ marginTop: 10, display: 'block' }}>
          No se pudo calcular la cobertura.
        </p>
      )}
      {cobQ.data && <Cobertura data={cobQ.data} />}
    </section>
  );
}

function Cobertura({ data }: { data: HubspotSyncCobertura }) {
  const { total, conHubspotId, porcentaje } = data;
  const pctClamped = Math.min(100, Math.max(0, porcentaje));
  // Sin titulares no hay nada que sincronizar: "0.0% · 0 de 0" en naranja se
  // leía como un problema cuando el padrón simplemente todavía no tiene gente.
  if (total === 0) {
    return (
      <p className="text-element-mid" style={{ fontSize: 13, marginTop: 12 }}>
        Este padrón todavía no tiene titulares para sincronizar.
      </p>
    );
  }
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 32, fontWeight: 700, color: '#FF7A00', lineHeight: 1 }}>
          {porcentaje.toFixed(1)}%
        </span>
        <span style={{ fontSize: 13, color: 'var(--color-element-mid)' }}>
          {conHubspotId.toLocaleString('es-AR')} de {total.toLocaleString('es-AR')} titulares en HubSpot
        </span>
      </div>
      <div
        style={{
          height: 6,
          background: 'rgba(0,0,0,0.08)',
          borderRadius: 999,
          overflow: 'hidden',
          marginTop: 10,
        }}
      >
        <div
          style={{
            width: `${pctClamped}%`,
            height: '100%',
            background: '#FF7A00',
            transition: 'width 0.4s ease-out',
          }}
        />
      </div>
    </div>
  );
}
